import React, { createContext, useContext, useState, useEffect } from "react";
import {
	signInWithEmailAndPassword,
	createUserWithEmailAndPassword,
	signOut,
	onAuthStateChanged,
	updatePassword,
} from "firebase/auth";
import {
	doc,
	setDoc,
	getDoc,
	collection,
	getDocs,
	updateDoc,
	query,
	where,
	serverTimestamp,
} from "firebase/firestore";
import { auth, secondaryAuth, db } from "../services/firebase";
import { getFriendlyAuthError } from "../utils/authErrors";

const AuthContext = createContext();

export function useAuth() {
	return useContext(AuthContext);
}

export function AuthProvider({ children }) {
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [initializing, setInitializing] = useState(true);

	// Listen for authentication state changes
	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
			setLoading(true);

			if (firebaseUser) {
				// Get additional user data from Firestore
				const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
				const userData = userDoc.data();

				// If user has been soft-deleted, sign them out
				if (userData?.isDeleted === true) {
					await signOut(auth);
					setUser(null);
					setLoading(false);
					setInitializing(false);
					return;
				}

				const userInfo = {
					uid: firebaseUser.uid,
					email: firebaseUser.email,
					name: userData?.name || "User",
					role: userData?.role || "user",
					isActive: userData?.isActive !== false,
					isBetaTester: userData?.isBetaTester === true,
					createdAt: userData?.createdAt,
					lastLogin: new Date().toISOString(),
				};

				// Update last login
				if (userData) {
					await updateDoc(doc(db, "users", firebaseUser.uid), {
						lastLogin: serverTimestamp(),
					});
				}

				setUser(userInfo);
			} else {
				setUser(null);
			}

			setLoading(false);
			setInitializing(false);
		});

		return unsubscribe;
	}, []);

	// Login function
	const login = async (email, password) => {
		setLoading(true);
		try {
			const result = await signInWithEmailAndPassword(auth, email, password);

			// Check if user is active in Firestore
			const userDoc = await getDoc(doc(db, "users", result.user.uid));
			const userData = userDoc.data();

			if (userData?.isDeleted === true) {
				await signOut(auth);
				throw new Error(
					"This account has been removed. Please contact your trainer.",
				);
			}

			if (userData && userData.isActive === false) {
				await signOut(auth);
				throw new Error("Account is inactive. Please contact your trainer.");
			}

			return result;
		} catch (error) {
			setLoading(false);
			console.error("Login error:", error);
			throw new Error(
				getFriendlyAuthError(error, "Login failed. Please try again."),
			);
		}
	};

	// Signup function - self-registration with access code + member cap
	const signup = async (email, password, name, accessCode) => {
		setLoading(true);
		try {
			// Fetch gym settings from config/gymSettings (public read)
			const settingsDoc = await getDoc(doc(db, "config", "gymSettings"));
			if (!settingsDoc.exists()) {
				throw new Error(
					"Registration is not configured yet. Please contact your trainer.",
				);
			}
			const settings = settingsDoc.data();

			// Validate access code (before creating any account)
			if (!accessCode || accessCode !== settings.gymCode) {
				throw new Error(
					"Invalid access code. Please contact your trainer for the correct code.",
				);
			}

			// Create Firebase Auth account first (so we're authenticated for Firestore reads)
			const result = await createUserWithEmailAndPassword(
				auth,
				email,
				password,
			);

			try {
				// Now authenticated — check member cap (exclude soft-deleted users)
				const usersSnapshot = await getDocs(
					query(collection(db, "users"), where("role", "==", "user")),
				);
				const activeUserCount = usersSnapshot.docs.filter(
					(d) => d.data().isDeleted !== true,
				).length;
				if (activeUserCount >= (settings.memberCap || 0)) {
					// Over cap — delete the auth account we just created and bail
					await result.user.delete();
					throw new Error(
						"The gym has reached its maximum number of members. Please contact your trainer.",
					);
				}

				// Create Firestore user document
				await setDoc(doc(db, "users", result.user.uid), {
					name,
					email,
					role: "user",
					isActive: true,
					createdAt: serverTimestamp(),
					lastLogin: null,
				});

				// Increment access code usage count
				try {
					const currentUsageCount = settings.codeUsageCount || 0;
					await updateDoc(doc(db, "config", "gymSettings"), {
						codeUsageCount: currentUsageCount + 1,
					});
				} catch (countError) {
					console.error("Failed to update code usage count:", countError);
				}

				setLoading(false);
				return result;
			} catch (innerError) {
				// If anything fails after auth creation, clean up the auth account
				try {
					await result.user.delete();
				} catch (deleteError) {
					console.error("Failed to clean up auth account:", deleteError);
				}
				throw innerError;
			}
		} catch (error) {
			setLoading(false);
			// Re-throw our custom errors as-is, wrap Firebase errors
			if (error.code) {
				throw new Error(
					getFriendlyAuthError(error, "Registration failed. Please try again."),
				);
			}
			throw error;
		}
	};

	// Create user function (Trainer only)
	// Uses secondaryAuth so the trainer's own session is not affected.
	const createUser = async (userData) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Unauthorized: Only trainers can create users");
		}

		setLoading(true);
		try {
			// Create the new user on the secondary auth instance so the
			// trainer stays signed in on the primary auth instance.
			const result = await createUserWithEmailAndPassword(
				secondaryAuth,
				userData.email,
				userData.password,
			);

			// Create user document in Firestore
			const userDoc = {
				name: userData.name,
				email: userData.email,
				role: "user",
				isActive: true,
				createdAt: serverTimestamp(),
				lastLogin: null,
				createdBy: user.uid,
			};

			await setDoc(doc(db, "users", result.user.uid), userDoc);

			// Sign out of the secondary auth instance to clean up
			await signOut(secondaryAuth);

			setLoading(false);
			return { ...userDoc, uid: result.user.uid };
		} catch (error) {
			setLoading(false);
			// Make sure secondary auth is cleaned up on error too
			try {
				await signOut(secondaryAuth);
			} catch (_) {}
			console.error("Create user error:", error);
			throw new Error(error.message || "Failed to create user");
		}
	};

	// Update user function (Trainer only)
	const updateUser = async (uid, updates) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Unauthorized: Only trainers can update users");
		}

		setLoading(true);
		try {
			await updateDoc(doc(db, "users", uid), {
				...updates,
				updatedAt: serverTimestamp(),
			});
			setLoading(false);
		} catch (error) {
			setLoading(false);
			console.error("Update user error:", error);
			throw new Error(error.message || "Failed to update user");
		}
	};

	// Soft-delete user function (Trainer only)
	// Marks the user as deleted instead of removing the document,
	// so the account can be restored later if needed.
	const deleteUser = async (uid) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Unauthorized: Only trainers can delete users");
		}

		try {
			const userDocRef = doc(db, "users", uid);
			const userDocSnap = await getDoc(userDocRef);
			const userData = userDocSnap.data();

			if (userData?.role === "trainer") {
				throw new Error("Cannot delete trainer accounts");
			}

			setLoading(true);

			await updateDoc(userDocRef, {
				isDeleted: true,
				isActive: false,
				deletedAt: serverTimestamp(),
				deletedBy: user.uid,
			});

			setLoading(false);
		} catch (error) {
			setLoading(false);
			console.error("Delete user error:", error);
			throw new Error(error.message || "Failed to delete user");
		}
	};

	// Restore a soft-deleted user (Trainer only)
	const restoreUser = async (uid) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Unauthorized: Only trainers can restore users");
		}

		try {
			setLoading(true);

			await updateDoc(doc(db, "users", uid), {
				isDeleted: false,
				isActive: true,
				deletedAt: null,
				deletedBy: null,
				restoredAt: serverTimestamp(),
			});

			setLoading(false);
		} catch (error) {
			setLoading(false);
			console.error("Restore user error:", error);
			throw new Error(error.message || "Failed to restore user");
		}
	};

	// Toggle beta tester status (Trainer only)
	const toggleBetaTester = async (uid) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Unauthorized: Only trainers can modify beta status");
		}

		try {
			const userDoc = await getDoc(doc(db, "users", uid));
			const userData = userDoc.data();

			setLoading(true);

			await updateDoc(doc(db, "users", uid), {
				isBetaTester: !userData.isBetaTester,
				updatedAt: serverTimestamp(),
			});

			setLoading(false);
		} catch (error) {
			setLoading(false);
			console.error("Toggle beta tester error:", error);
			throw new Error(error.message || "Failed to update beta tester status");
		}
	};

	// Toggle user status (Trainer only)
	const toggleUserStatus = async (uid) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Unauthorized: Only trainers can modify user status");
		}

		try {
			const userDoc = await getDoc(doc(db, "users", uid));
			const userData = userDoc.data();

			if (userData?.role === "trainer") {
				throw new Error("Cannot modify trainer status");
			}

			setLoading(true);

			await updateDoc(doc(db, "users", uid), {
				isActive: !userData.isActive,
				updatedAt: serverTimestamp(),
			});

			setLoading(false);
		} catch (error) {
			setLoading(false);
			console.error("Toggle status error:", error);
			throw new Error(error.message || "Failed to update user status");
		}
	};

	// Reset user password (Trainer only)
	const resetUserPassword = async (uid, newPassword) => {
		if (!user || user.role !== "trainer") {
			throw new Error("Unauthorized: Only trainers can reset passwords");
		}

		setLoading(true);
		try {
			// Note: This requires admin SDK on backend for production
			// For now, this is a placeholder - you'll need to implement
			// password reset via email or admin functions
			console.log("Password reset requested for:", uid);

			// You could send a password reset email instead:
			// await sendPasswordResetEmail(auth, userEmail);

			setLoading(false);
			throw new Error("Password reset via email - check inbox");
		} catch (error) {
			setLoading(false);
			throw new Error(error.message || "Failed to reset password");
		}
	};

	// Get users (Trainer only)
	const getUsers = () => {
		// This should be replaced with real-time listener in production
		// For now, implement in component with useEffect + Firebase listener
		return [];
	};

	// Update own profile (any authenticated user)
	const updateOwnProfile = async (updates) => {
		if (!user) throw new Error("You must be logged in");

		setLoading(true);
		try {
			await updateDoc(doc(db, "users", user.uid), {
				...updates,
				updatedAt: serverTimestamp(),
			});
			// Update local user state
			setUser((prev) => ({ ...prev, ...updates }));
		} catch (error) {
			console.error("Update profile error:", error);
			throw new Error(error.message || "Failed to update profile");
		} finally {
			setLoading(false);
		}
	};

	// Change own password (any authenticated user)
	const changeOwnPassword = async (newPassword) => {
		if (!auth.currentUser) throw new Error("You must be logged in");

		setLoading(true);
		try {
			await updatePassword(auth.currentUser, newPassword);
		} catch (error) {
			console.error("Change password error:", error);
			throw new Error(
				getFriendlyAuthError(error, "Failed to change password."),
			);
		} finally {
			setLoading(false);
		}
	};

	// Logout function
	const logout = async () => {
		setLoading(true);
		try {
			await signOut(auth);
		} catch (error) {
			console.error("Logout error:", error);
		}
		setLoading(false);
	};

	const value = {
		user,
		loading: loading || initializing,
		login,
		signup,
		logout,
		isTrainer: user?.role === "trainer",
		isUser: user?.role === "user",
		isBetaTester: user?.isBetaTester === true || user?.role === "trainer",
		// User Management Functions
		createUser,
		updateUser,
		deleteUser,
		restoreUser,
		toggleUserStatus,
		toggleBetaTester,
		resetUserPassword,
		getUsers,
		// Self-service
		updateOwnProfile,
		changeOwnPassword,
	};

	// Don't render children until Firebase auth state is determined
	if (initializing) {
		return (
			<div className="d-flex justify-content-center align-items-center min-vh-100">
				<div className="spinner-border text-primary" role="status">
					<span className="visually-hidden">Loading...</span>
				</div>
			</div>
		);
	}

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
